/**
 * Delivery Overview Card
 * 
 * Primary dashboard card showing delivery progress at a glance.
 * Displays purchased vs delivered leads with a progress bar and status badge.
 * 
 * PRINCIPLES:
 * - Client-friendly language (no internal system terms)
 * - Read-only, informational display
 * - Trust-building, reassuring tone
 */

import { Package, CheckCircle, Clock, AlertTriangle, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

export type DeliveryStatus = 'ON_TRACK' | 'SLIGHTLY_BEHIND' | 'AT_RISK' | 'COMPLETE' | 'OVER_DELIVERED';

interface DeliveryOverviewCardProps {
  totalPurchased: number;
  totalDelivered: number;
  remainingLeads: number;
  estimatedCompletionDate?: string;
  status: DeliveryStatus;
  daysRemaining?: number;
  rolloverLeads?: number;
}

const statusConfig: Record<DeliveryStatus, {
  label: string;
  icon: typeof CheckCircle;
  className: string;
  badgeVariant: "default" | "secondary" | "destructive" | "outline";
}> = {
  ON_TRACK: {
    label: "On Track",
    icon: CheckCircle,
    className: "text-green-600 bg-green-50 border-green-200",
    badgeVariant: "default",
  },
  SLIGHTLY_BEHIND: {
    label: "Slightly Behind",
    icon: Clock,
    className: "text-amber-600 bg-amber-50 border-amber-200",
    badgeVariant: "secondary",
  },
  AT_RISK: {
    label: "Behind Schedule",
    icon: AlertTriangle,
    className: "text-orange-600 bg-orange-50 border-orange-200",
    badgeVariant: "secondary",
  },
  COMPLETE: {
    label: "Complete",
    icon: CheckCircle,
    className: "text-primary bg-primary/10 border-primary/20",
    badgeVariant: "default",
  },
  OVER_DELIVERED: {
    label: "Bonus Delivered",
    icon: TrendingUp,
    className: "text-primary bg-primary/10 border-primary/20",
    badgeVariant: "default",
  },
};

export function DeliveryOverviewCard({
  totalPurchased,
  totalDelivered,
  remainingLeads,
  estimatedCompletionDate,
  status,
  daysRemaining,
  rolloverLeads,
}: DeliveryOverviewCardProps) {
  const config = statusConfig[status];
  const StatusIcon = config.icon;
  
  const progressPercent = totalPurchased > 0 
    ? Math.min(100, (totalDelivered / totalPurchased) * 100) 
    : 0;

  return (
    <Card className="border-2 border-primary/10 bg-gradient-to-br from-card to-primary/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Package className="w-6 h-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">Your Delivery Progress</CardTitle>
              <p className="text-sm text-muted-foreground">
                {remainingLeads > 0 
                  ? `${remainingLeads} leads remaining`
                  : "All leads delivered"
                }
              </p>
            </div>
          </div>
          
          <Badge 
            variant={config.badgeVariant}
            className={cn("flex items-center gap-1.5 px-3 py-1.5", config.className)}
          >
            <StatusIcon className="w-4 h-4" />
            {config.label}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Progress Section */}
        <div className="space-y-3">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-4xl font-bold text-foreground">{totalDelivered}</p>
              <p className="text-sm text-muted-foreground">
                of {totalPurchased}{(rolloverLeads ?? 0) > 0 ? ` + ${rolloverLeads} carried over` : ''} leads in your plan
              </p>
            </div>
            <p className="text-2xl font-semibold text-primary">{Math.round(progressPercent)}%</p>
          </div>
          
          <Progress value={progressPercent} className="h-3" />
          <p className="text-xs text-muted-foreground">Your plan includes {totalPurchased} leads. Delivery settings affect how quickly they arrive, not how many you receive.</p>
        </div>
        
        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 pt-2 border-t border-border">
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground">{totalPurchased}</p>
            <p className="text-xs text-muted-foreground">Contracted</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{totalDelivered}</p>
            <p className="text-xs text-muted-foreground">Delivered</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-accent">{remainingLeads}</p>
            <p className="text-xs text-muted-foreground">Remaining</p>
          </div>
        </div>
        
        {/* Estimated Completion */}
        {estimatedCompletionDate && daysRemaining && daysRemaining > 0 && (
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Estimated completion</span>
            </div>
            <span className="text-sm font-medium text-foreground">
              {estimatedCompletionDate} ({daysRemaining} days)
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
